import { useState, useMemo, useCallback, useEffect, useRef, type RefObject } from "react";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useRouter } from "expo-router";
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  type SharedValue,
} from "react-native-reanimated";
import { View, Text } from "@src/components";
import { Auth0UpsellBanner } from "@src/components/Auth0UpsellBanner";
import { DataStateView } from "@src/components/screen-state";
import { useCameras } from "@src/features/cameras";
import type { SortOption } from "@src/features/clips/types/camera";
import { useActionSheet } from "@expo/react-native-action-sheet";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { buildGroupedGridItems, useCameraCollection } from "@src/features/cameras/hooks/useCameraCollection";
import { getCameraKeyString, getCameraSourceAndFleet, isLegacyAppCamera } from "@src/utils/key-parser";
import { CameraListItem } from "./CameraListItem";
import { CameraSearchWithCrossTenant } from "./CameraSearchWithCrossTenant.native";
import LegacyKit from "modules/legacy-kit";
import * as ScreenOrientation from "expo-screen-orientation";
import { isWeb } from "@src/utils/web-helpers";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { showErrorToast } from "@src/utils/toast";
import { syncLegacyKitCredentials } from "@src/features/auth/utils/legacy-kit-auth";
import { FEATURE_FLAGS, useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { Button } from "@src/components/Button";
import { Icon } from "@src/components/Icon";
import { SignedSnapshot, useSnapshots } from "@visioni/a3s-media/app";
import { AssetCameraKey } from "@visioni/a3s-schema";

interface CameraListProps {
  siteId?: string;
  sortHandlerRef?: RefObject<(() => void) | null>;
  onSortHandlerReady?: (handler: () => void) => void;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  /** When set, parent controls sort (e.g. overview with native menu) */
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
  dockSearchToHeader?: boolean;
  /** When true, parent provides search/sort UI; list header is banner only */
  suppressHeaderSearch?: boolean;
  scrollY?: SharedValue<number>;
  /** When set, only cameras whose key is in this set are shown */
  cameraKeyFilter?: Set<string>;
}

type DisplayGroup = {
  site?: string;
  cameras: CameraApiResponse[];
};

type CameraCell = {
  camera: CameraApiResponse;
  snapshot?: SignedSnapshot;
  key: string;
};

type ListItem =
  | { type: "header"; site: string; key: string }
  | { type: "row"; cells: (CameraCell | undefined)[]; key: string };

const keyExtractor = (item: ListItem) => item.key;

const SORT_OPTIONS: SortOption[] = ["site", "nickname", "id"];
const LEGACY_OPEN_GUARD_MS = 800;

export function CameraList({
  siteId,
  sortHandlerRef,
  onSortHandlerReady,
  searchQuery: searchQueryProp,
  onSearchQueryChange,
  sortBy: sortByProp,
  onSortChange,
  dockSearchToHeader = false,
  suppressHeaderSearch = false,
  scrollY: scrollYProp,
  cameraKeyFilter,
}: CameraListProps) {
  const router = useRouter();
  const { theme } = useUnistyles();
  const { track } = useAmplitude();

  const { data: snapshots } = useSnapshots({ refetchInterval: 30000 });

  const { data: cameras, isLoading, isFetching, error, refetch: refetchCameras } = useCameras(siteId);
  const { showActionSheetWithOptions } = useActionSheet();
  const { getBooleanFlag } = useFeatureFlags();
  const hasSyncedLegacyCredentialsRef = useRef(false);
  const legacyOpenInFlightRef = useRef(false);
  const shouldSyncLegacyCredentials = !getBooleanFlag(FEATURE_FLAGS.SHOW_LEGACY_APP_ON_STARTUP);

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [internalSortBy, setInternalSortBy] = useState<SortOption>("site");
  const sortBy = sortByProp ?? internalSortBy;
  const setSortBy = useCallback((value: SortOption) => (onSortChange ?? setInternalSortBy)(value), [onSortChange]);
  const searchQuery = searchQueryProp ?? localSearchQuery;
  const setSearchQuery = onSearchQueryChange ?? setLocalSearchQuery;

  const internalScrollY = useSharedValue(0);
  const scrollY = scrollYProp ?? internalScrollY;

  const handleScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y;
    },
  });

  const searchHeaderStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [0, 80], [1, 0], Extrapolation.CLAMP),
    transform: [{ translateY: interpolate(scrollY.value, [0, 80], [0, -12], Extrapolation.CLAMP) }],
  }));

  const listHeaderContainerStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [0, 80], [1, 0], Extrapolation.CLAMP),
    transform: [{ translateY: interpolate(scrollY.value, [0, 80], [0, -12], Extrapolation.CLAMP) }],
  }));

  const handleSortPress = useCallback(() => {
    showActionSheetWithOptions(
      { options: ["Site", "Nickname", "ID", "Cancel"], cancelButtonIndex: 3, title: "Sort by" },
      (i) => {
        if (i !== undefined && i < 3) setSortBy(SORT_OPTIONS[i]);
      },
    );
  }, [showActionSheetWithOptions, setSortBy]);

  useEffect(() => {
    if (onSortHandlerReady) {
      onSortHandlerReady(handleSortPress);
    }
  }, [handleSortPress, onSortHandlerReady]);

  useEffect(() => {
    if (isWeb) return;
    if (!shouldSyncLegacyCredentials) return;
    if (isLoading) return;
    if (!cameras) return;
    if (hasSyncedLegacyCredentialsRef.current) return;

    hasSyncedLegacyCredentialsRef.current = true;
    syncLegacyKitCredentials();
  }, [cameras, isLoading, shouldSyncLegacyCredentials]);

  // Listen for legacy camera dismissal to lock orientation back to portrait
  useEffect(() => {
    if (isWeb) return;

    // @ts-expect-error types aren't defined
    const subscription = LegacyKit.addListener("onLegacyCameraDismissed", () => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
    });

    return () => subscription.remove();
  }, []);

  const handleCameraSelect = useCallback(
    (camera: CameraApiResponse) => async (cameraKey: string) => {
      // Use the camera's actual site ID from the camera data, not the prop
      const cameraSiteId = camera.site.id;
      const cameraTenantId = camera.site.tenant;
      const isLegacyApp = isLegacyAppCamera(camera);

      // Track camera click analytics
      const sourceAndFleet = getCameraSourceAndFleet(camera);
      track(ANALYTICS_EVENTS.CAMERA_CLICKED, {
        camera_id: cameraKey,
        ...(sourceAndFleet && {
          source: sourceAndFleet.source,
          generation: sourceAndFleet.fleet,
        }),
        tenant_id: cameraTenantId,
        site_id: cameraSiteId,
      });

      if (!isLegacyApp || isWeb) {
        router.push(`/tenants/${cameraTenantId}/sites/${cameraSiteId}/cameras/${cameraKey}` as never);
        return;
      }

      if (legacyOpenInFlightRef.current) {
        return;
      }

      legacyOpenInFlightRef.current = true;
      try {
        // Unlock orientation to allow legacy app to control portrait/landscape
        await ScreenOrientation.unlockAsync();

        // @ts-expect-error types aren't defined
        await LegacyKit.openCameraViewController({
          tenantId: cameraTenantId,
          siteId: cameraSiteId,
          cameraKey,
        });
        await new Promise((resolve) => setTimeout(resolve, LEGACY_OPEN_GUARD_MS));
      } catch (error) {
        console.error("[CameraList] Failed to open legacy camera:", error);
        showErrorToast({ message: "Unable to load camera." });
        // Lock back to portrait if there's an error
        await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
      } finally {
        legacyOpenInFlightRef.current = false;
      }
    },
    [router, track],
  );

  const camerasForCollection = useMemo(() => {
    if (!cameras) return undefined;
    if (!cameraKeyFilter) return cameras;
    return cameras.filter((c) => cameraKeyFilter.has(getCameraKeyString(c)));
  }, [cameras, cameraKeyFilter]);

  const { sortedCameras, filteredCameras } = useCameraCollection({
    cameras: camerasForCollection,
    searchQuery,
    sortBy: sortBy ? [sortBy] : [],
  });

  const displayGroups = useMemo((): DisplayGroup[] => {
    if (filteredCameras.length === 0) return [];

    if (sortBy === "site") {
      const groups = groupCamerasBySite(filteredCameras, sortBy);
      const hasMultipleGroups = groups.length > 1;

      return groups.map((group) => ({
        site: hasMultipleGroups ? group.siteName : undefined,
        cameras: group.cameras,
      }));
    }

    return [{ site: undefined, cameras: sortedCameras }];
  }, [filteredCameras, sortedCameras, sortBy]);

  const listItems = useMemo((): ListItem[] => {
    const numColumns = 2;
    return displayGroups.flatMap((group) => {
      const headerItem: ListItem[] =
        group.site ? [{ type: "header", site: group.site, key: `header-${group.site}` }] : [];

      const gridItems = buildGroupedGridItems(group.cameras, numColumns, snapshots);

      const rowItems: ListItem[] = [];
      for (let i = 0; i < gridItems.length; i += numColumns) {
        const cells: (CameraCell | undefined)[] = gridItems.slice(i, i + numColumns).map((item) =>
          item ?
            {
              camera: item.camera,
              snapshot: item.snapshot,
              key: AssetCameraKey.fromString(`${item.camera.key.asset}@${item.camera.key.id}`),
            }
          : undefined,
        );

        const firstCell = cells.find(Boolean);
        const rowKey = firstCell ? `row-${firstCell.key}-${i}` : `row-empty-${i}`;
        rowItems.push({ type: "row", cells, key: rowKey });
      }

      return [...headerItem, ...rowItems];
    });
  }, [displayGroups, snapshots]);

  const renderCameraCell = useCallback(
    (cell: CameraCell | undefined) =>
      cell ?
        <CameraListItem
          camera={cell.camera}
          siteId={cell.camera.site.id}
          onSelect={handleCameraSelect(cell.camera)}
          snapshot={cell.snapshot}
          style={styles.cameraItemFill}
        />
      : null,
    [handleCameraSelect],
  );

  const renderListItem = useCallback(
    ({ item }: { item: ListItem }) => {
      if (item.type === "header") {
        return (
          <View style={styles.headerSlot}>
            <Text textStyle="xs/medium" style={styles.headerText}>
              {item.site}
            </Text>
          </View>
        );
      }
      return (
        <View style={styles.cameraRow}>
          {item.cells.map((cell, i) => {
            if (!cell) {
              return null;
            }

            return (
              <View key={cell?.key ?? `empty-${i}`} style={styles.cameraSlot}>
                {renderCameraCell(cell)}
              </View>
            );
          })}
        </View>
      );
    },
    [renderCameraCell],
  );

  // getItemLayout estimates: rows now stretch to their tallest cell so actual height
  // can grow when camera names / IDs wrap. The estimate is good enough for FlatList's
  // virtualization heuristics (windowing / initial scroll); minor drift is tolerated.
  const thumbnailHeight = Math.ceil(theme.spacing["15xl"] * 1.465);
  const cameraInfoHeight = 2 + theme.spacing.lg * 2 + 24 + 4 + 16 + 34 + 26;
  const CAMERA_ROW_HEIGHT = thumbnailHeight + cameraInfoHeight + theme.spacing.lg;
  const HEADER_ROW_HEIGHT = theme.spacing["4xl"] + 16 + theme.spacing.lg;

  const itemLayouts = useMemo(() => {
    let currentY = 0;
    return listItems.map<{ length: number; offset: number }>((item) => {
      const length = item.type === "header" ? HEADER_ROW_HEIGHT : CAMERA_ROW_HEIGHT;
      const layout = { offset: currentY, length };
      currentY += length;
      return layout;
    });
  }, [listItems, CAMERA_ROW_HEIGHT, HEADER_ROW_HEIGHT]);

  const getItemLayout = useCallback(
    (_data: ArrayLike<ListItem> | null | undefined, index: number) => ({
      ...itemLayouts[index],
      index,
    }),
    [itemLayouts],
  );

  // Parent provides search (e.g. overview toolbar) → header is banner only
  const listHeaderComponent = useMemo(
    () =>
      suppressHeaderSearch ?
        <View style={styles.bannerContainer}>
          <Auth0UpsellBanner />
        </View>
      : dockSearchToHeader ?
        <>
          <Animated.View style={[styles.listHeaderContainer, listHeaderContainerStyle]}>
            <Animated.View style={[styles.searchHeader, searchHeaderStyle]}>
              <View flexDirection="row" alignItems="center" gap="md">
                <CameraSearchWithCrossTenant
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  size="medium"
                />
                <Button
                  label="Sort"
                  size="md"
                  variantColor="grey"
                  style={styles.sortButton}
                  textColor={theme.colors.textMuted}
                  onPress={() => sortHandlerRef?.current?.()}
                  iconRight={<Icon name="sort" size={20} color={theme.colors.textMuted} />}
                />
              </View>
            </Animated.View>
          </Animated.View>
          <View style={styles.bannerContainer}>
            <Auth0UpsellBanner />
          </View>
        </>
      : <View style={styles.bannerContainer}>
          <Auth0UpsellBanner />
        </View>,
    [
      suppressHeaderSearch,
      dockSearchToHeader,
      listHeaderContainerStyle,
      searchHeaderStyle,
      searchQuery,
      setSearchQuery,
      theme,
      sortHandlerRef,
    ],
  );

  const listEmptyComponent = useMemo(
    () =>
      dockSearchToHeader ?
        <View style={styles.emptyStateSlot} justifyContent="center" alignItems="center" py="15xl">
          <Icon name="search" size={64} color={theme.colors.textMuted} style={{ marginBottom: theme.spacing["4xl"] }} />
          <Text textStyle="lg/semibold">No cameras found</Text>
          <Text textStyle="md/regular" color="textMuted" mt="sm" textAlign="center">
            Try adjusting your search query
          </Text>
        </View>
      : undefined,
    [dockSearchToHeader, theme],
  );

  const renderCameraListContent = () => {
    const totalCameras = displayGroups.reduce((sum, group) => sum + group.cameras.length, 0);

    return (
      <View flex={1} bg="background">
        {!dockSearchToHeader && !suppressHeaderSearch && (
          <View px="5xl" pb="md" flexDirection="row" alignItems="center" gap="md">
            <CameraSearchWithCrossTenant searchQuery={searchQuery} onSearchQueryChange={setSearchQuery} size="medium" />
            <Button
              label="Sort"
              size="md"
              variantColor="grey"
              style={styles.sortButton}
              textColor={theme.colors.textMuted}
              onPress={() => sortHandlerRef?.current?.()}
              iconRight={<Icon name="sort" size={20} color={theme.colors.textMuted} />}
            />
          </View>
        )}

        {!dockSearchToHeader && totalCameras === 0 ?
          listEmptyComponent
        : <Animated.FlatList
            data={listItems}
            renderItem={renderListItem}
            keyExtractor={keyExtractor}
            getItemLayout={getItemLayout}
            removeClippedSubviews={false}
            windowSize={5}
            maxToRenderPerBatch={8}
            initialNumToRender={8}
            contentContainerStyle={styles.listContentContainer}
            showsVerticalScrollIndicator={false}
            ListEmptyComponent={listEmptyComponent}
            ListHeaderComponent={listHeaderComponent}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          />
        }
      </View>
    );
  };

  return (
    <View flex={1} testID="camera-list-screen">
      <DataStateView
        data={cameras}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetchCameras}
        isEmpty={(cameras) => cameras.length === 0}
        loadingMessage="Loading cameras..."
        errorTitle="Unable to load cameras"
        errorMessage="Please check your connection and try again."
        emptyState={{
          icon: "videoOff",
          title: "No cameras to display",
          message: "There are no cameras available for this site.",
          action: {
            label: "Refresh",
            onPress: () => void refetchCameras(),
          },
        }}
      >
        {renderCameraListContent}
      </DataStateView>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  listContentContainer: {
    flexGrow: 1,
    paddingHorizontal: theme.spacing["5xl"] - theme.spacing.md / 2,
    paddingBottom: theme.spacing["7xl"],
  },
  listHeaderContainer: {
    overflow: "hidden",
  },
  bannerContainer: {
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    paddingHorizontal: theme.spacing.md / 2,
  },
  searchHeader: {
    paddingBottom: theme.spacing.md,
    paddingHorizontal: theme.spacing.md / 2,
  },
  sortButton: { borderWidth: 1.5, backgroundColor: theme.colors.surface },
  headerSlot: {
    paddingHorizontal: theme.spacing.md / 2,
    paddingTop: theme.spacing["4xl"],
    paddingBottom: theme.spacing.lg,
  },
  headerText: {
    color: theme.colors.interactive.controls.default_midground,
  },
  cameraRow: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  cameraSlot: {
    flex: 1,
    paddingHorizontal: theme.spacing.md / 2,
    paddingBottom: theme.spacing.lg,
  },
  cameraItemFill: {
    flex: 1,
  },
  emptyStateSlot: {
    flex: 1,
  },
}));
